import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, List, Play, Menu, UserCircle, LogOut } from 'lucide-react';
import { useSequenceStore } from '../store/sequenceStore';
import AboutDialog from './AboutDialog';
import UserLoginDialog from './UserLoginDialog';
import PasswordPrompt from './PasswordPrompt';
import { generateUserFolder, findUserByCredentials } from '../services/userService';
import { PUBLIC_SEQUENCE_EDIT_PASSWORD } from '../config/constants';

function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const { user, setUser, selectedSequence, setSelectedSequence } = useSequenceStore();

  const handleLogin = async (fullName: string, email: string, phone: string) => {
    try {
      // Try to find existing user
      const existingUser = await findUserByCredentials(email, phone);
      
      if (existingUser) {
        // For existing users, just set the user in the store
        setUser(existingUser);
      } else {
        // For new users, create new user data
        const cloudFolder = generateUserFolder(fullName);
        const newUser = {
          id: crypto.randomUUID(),
          fullName,
          email,
          phone,
          cloudFolder
        };
        // For new users only, save the data and set in store
        setUser(newUser, true); // Pass true to indicate this is a new user
      }
      
      setIsLoginOpen(false);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Error during login:', error);
      alert('An error occurred during login. Please try again.');
    }
  };

  const handleLogout = () => {
    setUser(null);
    setIsDropdownOpen(false);
  };

  const handleDesignNavigation = () => {
    // If we're already on the design page, do nothing
    if (location.pathname.endsWith('design')) {
      return;
    }
    
    // Check if there's a selected sequence and it's public
    if (selectedSequence?.isPublic) {
      setShowPasswordPrompt(true);
    } else {
      navigate('design');
    }
  };

  const handlePasswordSubmit = (password: string) => {
    if (password === PUBLIC_SEQUENCE_EDIT_PASSWORD) {
      setShowPasswordPrompt(false);
      navigate('design');
    } else {
      alert('Incorrect password');
    }
  };

  return (
    <>
      <nav className="bg-white shadow-lg">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-full"
                >
                  <Menu size={20} className="sm:w-6 sm:h-6" />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50">
                    <button
                      onClick={() => {
                        setIsDropdownOpen(false);
                        setIsAboutOpen(true);
                      }}
                      className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      About
                    </button>
                    {user ? (
                      <>
                        <div className="px-4 py-2 text-sm text-gray-500 border-t">
                          {user.fullName}
                        </div>
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
                        >
                          <LogOut size={16} className="mr-2" />
                          Logout
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          setIsLoginOpen(true);
                        }}
                        className="flex items-center w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        <UserCircle size={16} className="mr-2" />
                        Login
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-1 sm:space-x-6">
                <button
                  onClick={() => navigate('/')}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-sm ${
                    location.pathname === '/' || location.pathname === ''
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Home size={18} className="sm:w-5 sm:h-5" />
                  <span className="hidden min-[402px]:inline">Catalog</span>
                </button>
                <button
                  onClick={handleDesignNavigation}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-sm ${
                    location.pathname.endsWith('design')
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <List size={18} className="sm:w-5 sm:h-5" />
                  <span className="hidden min-[402px]:inline">Design</span>
                </button>
                <button
                  onClick={() => navigate('player')}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-sm ${
                    location.pathname.endsWith('player')
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Play size={18} className="sm:w-5 sm:h-5" />
                  <span className="hidden min-[402px]:inline">Player</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <AboutDialog 
        isOpen={isAboutOpen}
        onClose={() => setIsAboutOpen(false)}
      />

      <UserLoginDialog
        isOpen={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
        onSubmit={handleLogin}
      />

      <PasswordPrompt
        isOpen={showPasswordPrompt}
        onSubmit={handlePasswordSubmit}
        onCancel={() => setShowPasswordPrompt(false)}
      />

      {isDropdownOpen && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </>
  );
}

export default Navigation;